/**
 * AI Recruit - Team Analytics Page
 * Manager-only dashboard showing aggregated team activity and member breakdowns.
 */

import React, { useState } from 'react'
import {
  FileText,
  Briefcase,
  Target,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  RefreshCw,
  Users,
  BarChart2,
  ChevronDown,
  Download,
} from 'lucide-react'
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useTeamDashboard, type MemberStatItem, type MemberJobBreakdownItem } from '../../hooks/useTeamDashboard'
import type { DateRangeOption } from '../../hooks/useDashboard'
import DateRangeFilter from '../../components/dashboard/DateRangeFilter'
import JobSubmissionsTracking from '../../components/dashboard/JobSubmissionsTracking'
import SourceAnalyticsCard from '../../components/dashboard/SourceAnalyticsCard'
import { exportTeamDashboard } from '../../utils/exportToExcel'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ROLE_BADGE: Record<string, string> = {
  senior_recruiter: 'bg-indigo-100 text-indigo-700',
  recruiter: 'bg-green-100 text-green-700',
  viewer: 'bg-gray-100 text-gray-600',
  manager: 'bg-blue-100 text-blue-700',
  admin: 'bg-red-100 text-red-700',
}

const RoleBadge: React.FC<{ role: string }> = ({ role }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
      ROLE_BADGE[role] ?? 'bg-gray-100 text-gray-600'
    }`}
  >
    {role.replace('_', ' ')}
  </span>
)

// ─── Member Activity Bar Chart ────────────────────────────────────────────────

const MemberBarChart: React.FC<{ members: MemberStatItem[] }> = ({ members }) => {
  const chartData = members.map((m) => ({
    name: m.full_name.split(' ')[0], // first name keeps Y-axis labels short
    Resumes: m.resumes_uploaded,
    Jobs: m.jobs_assigned,
    Matches: m.matches_applied,
  }))

  // Grow chart height with team size so bars never get squashed
  const chartHeight = Math.max(200, members.length * 52)

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-primary-600" />
            Member Activity
          </h3>
        </div>
      </div>
      <div className="card-body">
        {!members.length ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Users className="w-12 h-12 text-gray-400 mb-3" aria-hidden="true" />
            <p className="text-gray-600 text-sm">No team member activity yet</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
              barCategoryGap="30%"
              barGap={2}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={72} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Resumes" fill="#6366f1" radius={[0, 3, 3, 0]} />
              <Bar dataKey="Jobs"    fill="#0ea5e9" radius={[0, 3, 3, 0]} />
              <Bar dataKey="Matches" fill="#22c55e" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}


// ─── Status Donut Chart ───────────────────────────────────────────────────────

const StatusDonut: React.FC<{ items: { status: string; count: number; percentage: number; color: string }[] }> = ({
  items,
}) => {
  const total = items.reduce((s, i) => s + i.count, 0)

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium text-gray-900">Match Status Breakdown</h3>
      </div>
      <div className="card-body">
        {!items.length ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Target className="w-12 h-12 text-gray-400 mb-3" aria-hidden="true" />
            <p className="text-gray-600 text-sm">No matches have been applied yet</p>
          </div>
        ) : (
        <div className="grid grid-cols-1 gap-4">
          {/* Total above chart */}
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">{total}</div>
            <div className="text-sm text-gray-500">Total Matches</div>
          </div>

          {/* Pie chart with percentage labels */}
          <div className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={items.map((i) => ({ name: i.status, value: i.count, percentage: i.percentage, color: i.color }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={85}
                  paddingAngle={2}
                  dataKey="value"
                  label={(props: any) => {
                    const { cx, cy, midAngle, innerRadius, outerRadius, percentage } = props;
                    // Only show label if segment is large enough (>= 5%)
                    if (percentage < 5) return null;
                    
                    const RADIAN = Math.PI / 180;
                    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                    const y = cy + radius * Math.sin(-midAngle * RADIAN);
                    
                    return (
                      <text 
                        x={x} 
                        y={y} 
                        fill="white" 
                        textAnchor="middle" 
                        dominantBaseline="central"
                        className="text-sm font-semibold"
                      >
                        {`${percentage}%`}
                      </text>
                    );
                  }}
                  labelLine={false}
                >
                  {items.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="space-y-2">
            {items.map((item) => (
              <div key={item.status} className="flex items-center justify-between py-1">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                  <span className="text-sm font-medium text-gray-700">
                    {item.status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                  <span className="text-xs text-gray-400">({item.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>
  )
}

// ─── Member Jobs Breakdown ────────────────────────────────────────────────────

const getInitials = (name: string) =>
  name.split(' ').map((n) => n[0] ?? '').join('').slice(0, 2).toUpperCase()

const MemberJobsBreakdown: React.FC<{ breakdown: MemberJobBreakdownItem[] }> = ({ breakdown }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggle = (userId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(userId) ? next.delete(userId) : next.add(userId)
      return next
    })

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-600" />
            Teammate Job Assignments &amp; Submissions
          </h3>
          {/* <p className="text-xs text-gray-400 mt-0.5">
            Jobs assigned in this period · Submissions = matches that progressed beyond "Matched"
          </p> */}
        </div>
      </div>
      {!breakdown.length ? (
        <div className="card-body">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Briefcase className="w-12 h-12 text-gray-400 mb-3" aria-hidden="true" />
            <p className="text-gray-600 text-sm">No job assignments yet</p>
          </div>
        </div>
      ) : (
        <div className="card-body p-0">
          <div className="divide-y divide-gray-100">
            {breakdown.map((member) => {
              const isOpen = expanded.has(member.user_id)
              return (
                <div key={member.user_id}>
                  {/* Clickable member header */}
                  <button
                    type="button"
                    onClick={() => toggle(member.user_id)}
                    className="w-full flex items-center gap-3 px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-white">{getInitials(member.full_name)}</span>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 truncate">{member.full_name}</span>
                      <RoleBadge role={member.role} />
                    </div>
                    <div className="ml-auto flex items-center gap-3 flex-shrink-0">
                      {member.jobs.length > 0 && (
                        <span className="text-xs font-semibold text-primary-600">
                          {member.total_submissions} sub{member.total_submissions !== 1 ? 's' : ''}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        {member.jobs.length} job{member.jobs.length !== 1 ? 's' : ''}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </button>

                  {/* Expandable job list */}
                  {isOpen && (
                    member.jobs.length === 0 ? (
                      <div className="px-5 py-3 pl-16 text-xs text-gray-400 italic">
                        No jobs assigned in this period
                      </div>
                    ) : (
                      <table className="min-w-full">
                        <tbody className="divide-y divide-gray-50">
                          {member.jobs.map((job) => (
                            <tr key={job.job_uuid} className="hover:bg-gray-50 transition-colors">
                              <td className="pl-16 pr-4 py-2.5 w-full">
                                <p className="text-sm font-medium text-gray-800 truncate">{job.job_title}</p>
                                {job.job_id && (
                                  <p className="text-xs text-gray-400">{job.job_id}</p>
                                )}
                              </td>
                              <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-500">{job.client_name}</td>
                              <td className="px-5 py-2.5 whitespace-nowrap text-right">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                  job.submission_count > 0
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {job.submission_count} sub{job.submission_count !== 1 ? 's' : ''}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TeamAnalyticsPage: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRangeOption>('last_7_days')
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>()
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>()

  const { data, loading, error, refetch } = useTeamDashboard({ dateRange, customStartDate, customEndDate })

  const handleDateRangeChange = (range: DateRangeOption, startDate?: Date, endDate?: Date) => {
    setDateRange(range)
    setCustomStartDate(startDate)
    setCustomEndDate(endDate)
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Team Analytics</h1>
            <p className="text-gray-600 text-sm">Loading team data…</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="card-body">
                <div className="h-24 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <h1 className="text-2xl font-bold text-gray-900">Team Analytics</h1>
        <div className="card">
          <div className="card-body text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Team Analytics</h3>
            <p className="text-gray-600 mb-4 text-sm">{error}</p>
            <button onClick={refetch} className="btn-primary inline-flex items-center">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!data) return null

  // ── Empty state ──────────────────────────────────────────────────────────
  if (data.is_empty) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Team Analytics</h1>
            <p className="text-gray-500 text-sm">{data.team_name}</p>
          </div>
          <div className="flex items-center space-x-3">
            <DateRangeFilter
              selectedRange={dateRange}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onChange={handleDateRangeChange}
            />
            <button onClick={refetch} className="btn-outline btn-sm inline-flex items-center" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center py-16">
            <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No team activity yet</h3>
            <p className="text-gray-500 text-sm">
              {data.member_stats.length === 0
                ? 'Your team has no members assigned yet.'
                : 'No activity recorded for the selected date range.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ── Full dashboard ───────────────────────────────────────────────────────
  const statCards = [
    {
      key: 'resumes_uploaded',
      name: 'Team Resumes Uploaded',
      icon: FileText,
      stat: data.stats.resumes_uploaded,
    },
    {
      key: 'jobs_created',
      name: 'Team Jobs Assigned',
      icon: Briefcase,
      stat: data.stats.jobs_created,
    },
    {
      key: 'matches_applied',
      name: 'Team Matches Applied',
      icon: Target,
      stat: data.stats.matches_applied,
    },
  ]

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Analytics</h1>
          <p className="text-gray-500 text-sm mt-0.5">{data.team_name}</p>
        </div>
        <div className="flex items-center space-x-3">
          <DateRangeFilter
            selectedRange={dateRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onChange={handleDateRangeChange}
          />
          <button
            onClick={() => exportTeamDashboard(data, dateRange)}
            className="btn-outline btn-sm inline-flex items-center gap-1.5"
            title="Download Excel report"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={refetch}
            className="btn-outline btn-sm inline-flex items-center"
            title="Refresh team data"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {statCards.map(({ key, name, icon: Icon, stat }) => (
          <div key={key} className="card">
            <div className="card-body">
              <div className="flex flex-col items-center text-center">
                <div className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                  {stat.count.toLocaleString()}
                </div>
                <div className="flex items-center space-x-2 mb-3">
                  <Icon className="w-5 h-5 text-primary-600" />
                  <span className="text-sm font-medium text-gray-600">{name}</span>
                </div>
                {stat.trend && (
                  <div
                    className={`flex items-center text-sm font-medium ${
                      stat.trend.direction === 'up'
                        ? 'text-green-600'
                        : stat.trend.direction === 'down'
                        ? 'text-red-600'
                        : 'text-gray-600'
                    }`}
                  >
                    {stat.trend.direction === 'up' ? (
                      <TrendingUp className="w-4 h-4 mr-1" />
                    ) : stat.trend.direction === 'down' ? (
                      <TrendingDown className="w-4 h-4 mr-1" />
                    ) : null}
                    {stat.trend.type === 'percentage'
                      ? `${stat.trend.value.toFixed(1)}%`
                      : stat.trend.value.toFixed(0)}{' '}
                    {stat.trend.comparison_period}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Member Activity Bar Chart */}
      <MemberBarChart members={data.member_stats} />

      {/* Status breakdown + Job Submissions side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StatusDonut items={data.status_breakdown} />
        <JobSubmissionsTracking jobSubmissions={data.job_submissions} />
      </div>

      {/* Source Analytics - Full width */}
      <SourceAnalyticsCard 
        resumeSources={data.resume_sources}
        jobSources={data.job_sources}
      />

      {/* Teammate job assignments & submissions */}
      <MemberJobsBreakdown breakdown={data.member_job_breakdown} />
    </div>
  )
}

export default TeamAnalyticsPage
